<script setup lang="ts">
import {reactive, ref, watch} from "vue";
import {useToast} from "primevue/usetoast";
import {Button, Card, Checkbox, ColorPicker, Dialog, InputText, Label, Textarea} from "@/Components/Library";
import {AnonymizationSelect} from "@/Components/Forms";
import {cloneGroup} from "@/Services/groups";

const props = defineProps<{
    group: any;
}>();

const visible = defineModel<boolean>("visible", {required: true});
const toast = useToast();
const cloning = ref(false);
const form = reactive({
    name: "",
    description: "",
    important_info: "",
    color: "emerald",
    anonymization: "full_name",
    copy_members: true,
    copy_invited_members: true,
    copy_questions: true,
});

const resetForm = () => {
    Object.assign(form, {
        name: `${props.group.name} (copy)`,
        description: props.group.description ?? "",
        important_info: props.group.important_info ?? "",
        color: props.group.color?.slug ?? props.group.color ?? "emerald",
        anonymization: props.group.anonymization?.slug ?? props.group.anonymization ?? "full_name",
        copy_members: true,
        copy_invited_members: true,
        copy_questions: true,
    });
};

const submit = async () => {
    try {
        cloning.value = true;
        await cloneGroup(props.group.id, form);
        visible.value = false;
        toast.add({
            severity: "success",
            summary: "Group clone started",
            detail: "You will be notified when the new group is ready.",
            life: 5000,
        });
    } finally {
        cloning.value = false;
    }
};

watch(visible, (isVisible) => {
    if (isVisible) {
        resetForm();
    }
});
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        dismissable-mask
        class="w-full max-w-2xl overflow-hidden"
        header="Clone group"
    >
        <form class="flex flex-col gap-4" @submit.prevent="submit">
            <Card>
                <div class="flex flex-col gap-4">
                    <div class="flex flex-col gap-1">
                        <Label for="clone-group-name" required>Name</Label>
                        <InputText
                            id="clone-group-name"
                            v-model="form.name"
                            placeholder="Enter a name for your group"
                            fluid
                            required
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <Label for="clone-group-description">Description</Label>
                        <Textarea
                            id="clone-group-description"
                            v-model="form.description"
                            placeholder="Enter a description for your group"
                            fluid
                            auto-resize
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <Label for="clone-group-important-info">Important Info</Label>
                        <Textarea
                            id="clone-group-important-info"
                            v-model="form.important_info"
                            placeholder="Add any important information for group members"
                            fluid
                            auto-resize
                        />
                    </div>
                </div>
            </Card>

            <Card>
                <h3 class="mb-2 font-bold">Color</h3>
                <ColorPicker v-model="form.color" />
            </Card>

            <Card>
                <h3 class="mb-2 font-bold">Anonymization</h3>
                <AnonymizationSelect v-model="form.anonymization" />
            </Card>

            <Card>
                <h3 class="mb-3 font-bold">Copy from the existing group</h3>
                <div class="flex flex-col gap-3">
                    <div class="flex items-center gap-2">
                        <Checkbox v-model="form.copy_members" input-id="clone-group-members" binary />
                        <Label for="clone-group-members">Members</Label>
                    </div>
                    <div class="flex items-center gap-2">
                        <Checkbox v-model="form.copy_invited_members" input-id="clone-group-invited-members" binary />
                        <Label for="clone-group-invited-members">Invited members</Label>
                    </div>
                    <div class="flex items-center gap-2">
                        <Checkbox v-model="form.copy_questions" input-id="clone-group-questions" binary />
                        <Label for="clone-group-questions">Questions</Label>
                    </div>
                </div>
            </Card>

            <Button type="submit" class="w-full bg-askr-green text-white" :loading="cloning">Clone group</Button>
        </form>
    </Dialog>
</template>
